import Chip from '@components/chip'
import { type CollectionProduct } from '@lib/product-collection'
import { StringsContext } from '@lib/strings-context'
import { useContext, useMemo } from 'react'

interface ProductCardChipsProps {
  product: CollectionProduct
}

const ProductCardChips = ({ product }: ProductCardChipsProps) => {
  const strings = useContext(StringsContext)

  const isOutOfStock = useMemo(() => {
    if (!product.variants) {
      return
    }

    return product.variants.every((variant) => !variant.inStock)
  }, [product])

  const formattedComparePrice = useMemo(() => {
    if (!product.comparePrice) {
      return
    }

    const discount =
      (product.comparePrice - product.price) / product.comparePrice
    const discountPercent = Math.ceil(discount * 100)

    return strings.productDiscountText.replace(
      /{percent}/gi,
      discountPercent.toString(),
    )
  }, [product, strings])

  const isPreorderActive = useMemo(() => {
    if (
      !product ||
      !product.enablePreorder ||
      !product.preorderCountdownTarget ||
      !product.preorderDeliveryDate
    ) {
      return false
    }

    // Check if coundown timer has reached 0
    const date = Date.parse(product.preorderCountdownTarget)
    const now = Date.now()

    return date > now
  }, [product])

  const isAvailabilityCountdownActive = useMemo(() => {
    if (
      !product ||
      !product.enableAvailabilityCountdown ||
      !product.availabilityCountdownTarget
    ) {
      return false
    }

    const date = Date.parse(product.availabilityCountdownTarget)
    const now = Date.now()

    return date > now
  }, [product])

  return (
    <>
      {isPreorderActive && (
        <Chip label={strings.productPreorderLabel} color="yellow" />
      )}
      {isAvailabilityCountdownActive && !!strings.productAvailabilityLabel && (
        <Chip label={strings.productAvailabilityLabel} color="yellow" />
      )}
      {!isOutOfStock &&
        !isPreorderActive &&
        !isAvailabilityCountdownActive &&
        !!formattedComparePrice && (
          <Chip label={formattedComparePrice} color="black-yellow" />
        )}
      {isOutOfStock &&
        !isPreorderActive &&
        !isAvailabilityCountdownActive && (
          <Chip label={strings.productOutOfStock} color="gray" />
        )}
      {product.gender === 'women' && !!strings.productWomensLabel && (
        <Chip label={strings.productWomensLabel} color="black" />
      )}
    </>
  )
}

export default ProductCardChips
